
import React, { useState, useMemo } from 'react';
import { Shift } from '../types';
import { calculateHoursBreakdown } from '../services/calculationService';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale/pt-BR';
import { Calendar, Clock, Trash2, Copy, AlertTriangle, Pencil, FilterX, Coins, Moon, Sun, Globe, Coffee, Wallet, CheckCircle2, ShoppingCart, TrendingUp, PiggyBank, ArrowDownCircle, FileText, Percent, Timer, ClipboardCheck, X, Truck, AlertCircle } from 'lucide-react';

interface Props {
    shifts: Shift[];
    onDelete: (id: string) => void;
    onDuplicate: (shift: Shift) => void;
    onEdit: (shift: Shift) => void;
    onConfirmAdjust?: (shift: Shift) => void;
    onRequirePremium?: (featureName: string) => void;
    isLoading?: boolean;
    isPrivacyMode?: boolean;
    userGeneration?: 'OLD' | 'NEW';
    isPremiumOrVip?: boolean;
}

type FilterType = 'ALL' | 'DAY' | 'NIGHT' | 'BILINGUAL' | 'TIPS' | 'OFF' | 'NO_SHOW' | 'EXPENSE' | 'EXTRA' | 'FEES' | 'UNPAID_BREAK';

const RATE_BASE = 20; // Base rate matching calculationService

interface WeeklyGroup {
    weekStart: Date;
    weekEnd: Date;
    shifts: Shift[];
    summaryValue: number;
    summaryHours: number;
}

// Helpers to replace date-fns functions that are failing to import
const getStartOfWeek = (date: Date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = (day === 0 ? -6 : 1) - day; // Adjust for Monday start (1)
    d.setDate(d.getDate() + diff);
    d.setHours(0, 0, 0, 0);
    return d;
};

const getEndOfWeek = (date: Date) => {
    const d = getStartOfWeek(date);
    d.setDate(d.getDate() + 6);
    d.setHours(23, 59, 59, 999);
    return d;
};

export const calculateRestValue = (minutes: number, baseRate: number = RATE_BASE) => {
    const centesimalUnits = (minutes / 60) * 100;
    const valuePerUnit = baseRate / 100; // x1 — R$ 0,20 per centesimal
    return centesimalUnits * valuePerUnit;
};

// ==================== ADJUST SHIFT MODAL ====================
interface AdjustShiftModalProps {
    shift: Shift;
    onClose: () => void;
    onConfirm: (adjusted: Shift) => void;
    parseDate: (dateStr: string) => Date;
}

const AdjustShiftModal: React.FC<AdjustShiftModalProps> = ({ shift, onClose, onConfirm, parseDate, userGeneration = 'OLD' }) => {
    const [adjustRealStart, setAdjustRealStart] = useState(shift.startTime);
    const [adjustRealEnd, setAdjustRealEnd] = useState(shift.endTime);
    const [adjustBreak, setAdjustBreak] = useState((shift.unpaidBreakMinutes || 0).toString());
    const [carArrivalTime, setCarArrivalTime] = useState(shift.carArrivalTime || "");
    const [loginAppTime, setLoginAppTime] = useState(shift.loginAppTime || "");
    const [loginTelegramTime, setLoginTelegramTime] = useState(shift.loginTelegramTime || "");
    const [logoutTelegramTime, setLogoutTelegramTime] = useState(shift.logoutTelegramTime || "");

    // Parse time to minutes since midnight
    const toMin = (t: string) => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };

    const plannedStartStr = shift.plannedStartTime || shift.startTime;
    const plannedEndStr = shift.plannedEndTime || shift.endTime;

    const plannedStartMin = toMin(plannedStartStr);
    const plannedEndMin = toMin(plannedEndStr);
    const realStartMin = toMin(adjustRealStart);
    const realEndMin = toMin(adjustRealEnd);

    // Early arrival (bonus): if started before planned
    const earlyMinutes = Math.max(0, plannedStartMin - realStartMin);

    // Late arrival: if started after reference + 5 min tolerance
    // Reference is max(plannedStart, carArrival)
    const arrivalMin = carArrivalTime ? toMin(carArrivalTime) : 0;
    const referenceStartMin = Math.max(plannedStartMin, arrivalMin);
    
    const lateRaw = Math.max(0, realStartMin - referenceStartMin);
    const lateMinutes = lateRaw > 5 ? lateRaw : 0;
    
    // Check if garage delay saved driver
    const savedByGarage = arrivalMin > plannedStartMin && realStartMin <= arrivalMin + 5;

    // Extra end (worked past planned end)
    let extraEndMin = 0;
    if (plannedEndMin < plannedStartMin) {
        // Shift crosses midnight
        const pEndAdj = plannedEndMin + 1440;
        const rEndAdj = realEndMin <= realStartMin ? realEndMin + 1440 : realEndMin;
        extraEndMin = Math.max(0, rEndAdj - pEndAdj);
    } else {
        extraEndMin = Math.max(0, realEndMin - plannedEndMin);
    }

    const now = new Date();
    const currentMin = now.getHours() * 60 + now.getMinutes();
    const isShiftToday = format(parseDate(shift.date), 'yyyy-MM-dd') === format(now, 'yyyy-MM-dd');
    const isBeforePlannedStart = isShiftToday && currentMin < plannedStartMin;
    
    // Early departure (worked less than planned end) based on Telegram logout if provided, otherwise real end
    let earlyDepartureMin = 0;
    const logoutTimeStr = logoutTelegramTime || adjustRealEnd;
    if (logoutTimeStr) {
        const logoutMin = toMin(logoutTimeStr);
        if (plannedEndMin < plannedStartMin) {
            const pEnd = plannedEndMin + 1440;
            const rEnd = logoutMin <= realStartMin ? logoutMin + 1440 : logoutMin;
            earlyDepartureMin = Math.max(0, pEnd - rEnd);
        } else {
            earlyDepartureMin = Math.max(0, plannedEndMin - logoutMin);
        }
    }

    const totalExtraMinutes = earlyMinutes + extraEndMin;
    const breakMin = parseFloat(adjustBreak) || 0;

    // Centesimal conversions
    const extraCentesimal = (totalExtraMinutes / 60) * 100;
    const lateCentesimal = (lateMinutes / 60) * 100;
    const earlyDepartureCentesimal = (earlyDepartureMin / 60) * 100;
    const breakCentesimal = (breakMin / 60) * 100;

    // Financial values
    const extraValue = extraCentesimal * 0.20;
    const lateValue = lateCentesimal * 0.40; // x2 penalty after 5min tolerance
    const earlyDepartureValue = earlyDepartureCentesimal * 0.40; // x2 penalty for leaving early
    const breakValue = breakCentesimal * 0.20; // x1 bonus for unused break
    const netAudit = extraValue + breakValue - lateValue - earlyDepartureValue;

    const handleConfirmAdjust = () => {
        const adjusted: Shift = {
            ...shift,
            plannedStartTime: plannedStartStr,
            plannedEndTime: plannedEndStr,
            startTime: adjustRealStart,
            endTime: adjustRealEnd,
            unpaidBreakMinutes: breakMin,
            carArrivalTime: carArrivalTime,
            loginAppTime: loginAppTime,
            loginTelegramTime: loginTelegramTime,
            logoutTelegramTime: logoutTelegramTime
        };
        onConfirm(adjusted);
    };

    return (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 backdrop-blur-sm animate-fade-in">
            <div className="bg-slate-800 p-6 rounded-2xl border border-slate-700 shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto custom-scrollbar">
                {/* Header */}
                <div className="flex justify-between items-start mb-6">
                    <div>
                        <h3 className="text-lg font-black text-white flex items-center gap-2">
                            <ClipboardCheck className="w-5 h-5 text-emerald-500" /> Ajuste Real de Turno
                        </h3>
                        <span className="text-[10px] text-slate-400 bg-slate-900/50 border border-slate-700/50 px-2 py-0.5 rounded uppercase mt-1.5 inline-block">
                            Cálculo: {userGeneration === 'NEW' ? 'Geração Nova' : 'Geração Antiga'}
                        </span>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-white mt-1"><X className="w-5 h-5" /></button>
                </div>

                {/* Aviso Beta */}
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 mb-4">
                    <p className="text-amber-400 text-xs font-bold text-center">
                        ⚠️ MODO TESTE — Função em desenvolvimento para assinantes Elite
                    </p>
                </div>

                {/* Planned (read-only) */}
                <div className="bg-slate-900/50 p-4 rounded-xl border border-slate-700 mb-4">
                    <p className="text-[10px] uppercase font-bold text-slate-500 tracking-wider mb-2">📅 Planejado ({format(parseDate(shift.date), "dd/MM", { locale: ptBR })})</p>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-[10px] text-slate-500 block mb-1">Início</label>
                            <div className="bg-slate-800 px-3 py-2 rounded-lg text-white text-sm font-mono border border-slate-600">{plannedStartStr}</div>
                        </div>
                        <div>
                            <label className="text-[10px] text-slate-500 block mb-1">Fim</label>
                            <div className="bg-slate-800 px-3 py-2 rounded-lg text-white text-sm font-mono border border-slate-600">{plannedEndStr}</div>
                        </div>
                    </div>
                </div>

                {/* Real (editable) */}
                <div className="bg-slate-900 p-5 rounded-2xl border border-emerald-500/20 mb-6 shadow-xl space-y-4">
                    <div className="flex items-center gap-2 mb-2">
                        <div className="w-5 h-5 bg-emerald-500/20 rounded flex items-center justify-center">
                            <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                        </div>
                        <h4 className="text-[10px] font-bold text-emerald-500 uppercase tracking-widest">Realizado</h4>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="text-[10px] text-slate-400 block mb-1 font-medium">Login Rhino App</label>
                            <input 
                                type="time"
                                value={loginAppTime}
                                onChange={(e) => setLoginAppTime(e.target.value)}
                                className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white font-mono text-base focus:border-emerald-500/50 outline-none transition-all"
                            />
                        </div>
                        <div>
                            <label className="text-[10px] text-slate-400 block mb-1 font-medium">Login Telegram</label>
                            <input 
                                type="time"
                                value={loginTelegramTime}
                                onChange={(e) => setLoginTelegramTime(e.target.value)}
                                className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white font-mono text-base focus:border-emerald-500/50 outline-none transition-all"
                            />
                        </div>
                        <div className="col-span-2">
                            <label className="text-[10px] text-slate-400 block mb-1 font-medium">Início Real (Consolidado)</label>
                            <input 
                                type="time"
                                value={adjustRealStart}
                                onChange={(e) => setAdjustRealStart(e.target.value)}
                                className="w-full bg-slate-900 border border-emerald-500/30 rounded-xl px-4 py-3 text-emerald-400 font-bold font-mono text-lg focus:border-emerald-500 outline-none transition-all"
                            />
                            <p className="text-[9px] text-slate-500 mt-1 italic text-center">O horário real deve ser o do Token de Verificação enviado pela Rhino</p>
                        </div>
                        <div>
                            <label className="text-[10px] text-slate-400 block mb-1 font-medium">Fim Real (Deputy)</label>
                            <input 
                                type="time"
                                value={adjustRealEnd}
                                onChange={(e) => setAdjustRealEnd(e.target.value)}
                                className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white font-mono text-base focus:border-emerald-500/50 outline-none transition-all"
                            />
                        </div>
                        <div>
                            <label className="text-[10px] text-slate-400 block mb-1 font-medium">Logout Telegram</label>
                            <input 
                                type="time"
                                value={logoutTelegramTime}
                                onChange={(e) => setLogoutTelegramTime(e.target.value)}
                                className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white font-mono text-base focus:border-emerald-500/50 outline-none transition-all"
                            />
                        </div>
                    </div>

                    <div className="p-4 bg-slate-800/40 rounded-xl border border-slate-700/50 space-y-4">
                        <div className="space-y-3">
                            <label className="text-[10px] text-slate-400 font-medium uppercase tracking-widest block">Logística: Chegada do Carro</label>
                            
                            <div className="flex gap-2">
                                <input 
                                    type="time"
                                    value={carArrivalTime}
                                    onChange={(e) => setCarArrivalTime(e.target.value)}
                                    className="flex-1 bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white font-mono text-base focus:border-blue-500/50 outline-none transition-all shadow-inner"
                                />
                                <button 
                                    onClick={() => setCarArrivalTime(format(new Date(), 'HH:mm'))}
                                    disabled={isBeforePlannedStart}
                                    className={`flex-[2] font-bold rounded-xl px-4 py-3 flex items-center justify-center gap-2 shadow-lg active:scale-95 transition-all ${
                                        isBeforePlannedStart 
                                        ? 'bg-slate-700 text-slate-500 cursor-not-allowed shadow-none' 
                                        : 'bg-blue-600 hover:bg-blue-500 text-white shadow-blue-900/20'
                                    }`}
                                >
                                    <Truck className="w-5 h-5" />
                                    {isBeforePlannedStart ? 'Aguarde o Início' : 'O Carro Chegou Agora!'}
                                </button>
                            </div>

                            {isBeforePlannedStart && (
                                <p className="text-[9px] text-slate-500 italic mt-1">O botão será ativado às {shift.plannedStartTime}.</p>
                            )}

                            {arrivalMin > plannedStartMin && (
                                <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg flex items-center gap-2">
                                    <AlertCircle className="w-4 h-4 text-blue-400" />
                                    <p className="text-[10px] text-blue-300 font-medium">Atraso da Logística detectado: Isenção de multa ativa.</p>
                                </div>
                            )}
                        </div>

                        <div>
                            <label className="text-[10px] text-slate-400 block mb-1 font-medium">Descanso Salvo (min)</label>
                            <input 
                                type="number"
                                value={adjustBreak}
                                onChange={(e) => setAdjustBreak(e.target.value)}
                                className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-2 text-white font-mono text-sm focus:border-emerald-500/50 outline-none transition-all"
                                placeholder="Minutos..."
                            />
                        </div>
                    </div>
                </div>

                {/* Audit Summary */}
                <div className="bg-slate-900/50 p-4 rounded-xl border border-slate-700 mb-6 space-y-3">
                    <p className="text-[10px] uppercase font-bold text-slate-500 tracking-wider mb-1">📊 Resumo de Auditoria</p>
                    
                    {totalExtraMinutes > 0 && (
                        <div className="flex justify-between items-center text-sm">
                            <span className="text-green-400 flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" /> Tempo Extra ({totalExtraMinutes}min = {extraCentesimal.toFixed(0)} cent.)</span>
                            <span className="text-green-400 font-bold">+ R$ {extraValue.toFixed(2)}</span>
                        </div>
                    )}

                    {breakMin > 0 && (
                        <div className="flex justify-between items-center text-sm">
                            <span className="text-emerald-400 flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Pausa Salva ({breakMin}min = {breakCentesimal.toFixed(0)} cent.)</span>
                            <span className="text-emerald-400 font-bold">+ R$ {breakValue.toFixed(2)}</span>
                        </div>
                    )}

                    {lateMinutes > 0 && (
                        <div className="flex justify-between items-center text-sm bg-red-900/20 p-2 rounded-lg border border-red-800/30">
                            <span className="text-red-400 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> Atraso Entrada ({lateMinutes}min = {lateCentesimal.toFixed(0)} cent. <span className="font-black">×2</span>)</span>
                            <span className="text-red-400 font-bold">- R$ {lateValue.toFixed(2)}</span>
                        </div>
                    )}

                    {earlyDepartureMin > 0 && (
                        <div className="flex justify-between items-center text-sm bg-red-900/20 p-2 rounded-lg border border-red-800/30 mt-2">
                            <span className="text-red-400 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> Saída Antecipada ({earlyDepartureMin}min = {earlyDepartureCentesimal.toFixed(0)} cent. <span className="font-black">×2</span>)</span>
                            <span className="text-red-400 font-bold">- R$ {earlyDepartureValue.toFixed(2)}</span>
                        </div>
                    )}

                    {lateRaw > 0 && lateRaw <= 5 && (
                        <div className="flex items-center gap-2 p-3 bg-blue-500/10 border border-blue-500/20 rounded-xl">
                            <AlertCircle className="w-4 h-4 text-blue-400" />
                            <p className="text-[10px] text-blue-300">Tolerância Rhino de 5min ativa. Sem multa X2.</p>
                        </div>
                    )}

                    {lateRaw > 5 && arrivalMin > plannedStartMin && realStartMin <= arrivalMin + 5 && (
                        <div className="flex items-center gap-2 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
                            <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                            <p className="text-[10px] text-emerald-300">Atraso isento. O carro chegou às {carArrivalTime}.</p>
                        </div>
                    )}

                    <div className="h-px bg-slate-700 my-2" />
                    <div className="flex justify-between items-center">
                        <span className="text-white font-bold text-sm">Saldo do Ajuste</span>
                        <span className={`text-lg font-black ${netAudit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            {netAudit >= 0 ? '+' : ''} R$ {netAudit.toFixed(2)}
                        </span>
                    </div>
                </div>

                {/* Actions */}
                <div className="flex gap-3">
                    <button onClick={onClose} className="flex-1 bg-slate-700 hover:bg-slate-600 text-white font-medium py-3 rounded-xl transition-colors">Cancelar</button>
                    <button onClick={handleConfirmAdjust} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3 rounded-xl transition-colors shadow-lg shadow-emerald-900/20 flex items-center justify-center gap-2">
                        <CheckCircle2 className="w-5 h-5" /> Confirmar Ajuste
                    </button>
                </div>
            </div>
        </div>
    );
};

const toMin = (t: string) => {
    if (!t) return 0;
    const parts = t.split(':');
    if (parts.length < 2) return 0;
    const [h, m] = parts.map(Number);
    if (isNaN(h) || isNaN(m)) return 0;
    return h * 60 + m;
};

export const HistoryView: React.FC<Props> = ({ shifts, onDelete, onDuplicate, onEdit, onConfirmAdjust, onRequirePremium, isLoading = false, isPrivacyMode = false, userGeneration = 'OLD', isPremiumOrVip = false }) => {
    const [selectedMonth, setSelectedMonth] = useState<string>(''); // Format: YYYY-MM
    const [filterType, setFilterType] = useState<FilterType>('ALL');
    const [shiftToDelete, setShiftToDelete] = useState<Shift | null>(null);
    const [shiftToAdjust, setShiftToAdjust] = useState<Shift | null>(null);
    const [expandedDetails, setExpandedDetails] = useState<Record<string, boolean>>({});

    const toggleDetails = (id: string) => {
        setExpandedDetails(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const formatValue = (val: number, decimals = 0) => {
        if (isPrivacyMode) return '****';
        return `R$ ${val.toFixed(decimals)}`;
    };

    const parseDate = (str: string) => {
        if (!str) return new Date();
        const cleanStr = str.substring(0, 10);

        // Support YYYY-MM-DD
        if (cleanStr.includes('-') && cleanStr.indexOf('-') === 4) {
            const [y, m, d] = cleanStr.split('-').map(Number);
            return new Date(y, m - 1, d);
        }

        // Support DD/MM/YYYY or DD-MM-YYYY
        if (cleanStr.includes('/') || (cleanStr.includes('-') && cleanStr.indexOf('-') === 2)) {
            const separator = cleanStr.includes('/') ? '/' : '-';
            const [d, m, y] = cleanStr.split(separator).map(Number);
            if (y > 31) { // Basic check for year position
                return new Date(y, m - 1, d);
            }
        }

        // Fallback
        const d = new Date(cleanStr);
        return isNaN(d.getTime()) ? new Date() : d;
    };

    const formatHoursToHHMM = (decimalHours: number): string => {
        const h = Math.floor(decimalHours);
        const m = Math.round((decimalHours - h) * 60);
        return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
    };

    // Helper to calculate simple duration from HH:mm strings for display purposes
    const getDurationInHours = (startStr: string, endStr: string) => {
        if (!startStr || !endStr) return 0;
        const [h1, m1] = startStr.split(':').map(Number);
        const [h2, m2] = endStr.split(':').map(Number);
        let minutes = (h2 * 60 + m2) - (h1 * 60 + m1);
        if (minutes < 0) minutes += 1440; // Handle overnight simple wrap
        return minutes / 60;
    };

    // 1. Month Logic
    const availableMonths = useMemo(() => {
        const months = new Set<string>();
        shifts.forEach(shift => {
            if (shift.date && shift.date.length >= 10) {
                // Safe substring
                const monthStr = shift.date.substring(0, 7);
                months.add(monthStr);
            }
        });
        return Array.from(months).sort().reverse();
    }, [shifts]);

    // 2. Base Filter: Filter by Month FIRST
    const monthlyShifts = useMemo(() => {
        return shifts.filter(shift => {
            if (!shift.date) return false;
            if (selectedMonth && shift.date.substring(0, 7) !== selectedMonth) return false;
            return true;
        }).sort((a, b) => b.date.localeCompare(a.date));
    }, [shifts, selectedMonth]);

    // 3. Calculate Totals for the Unified Metrics Bar (Based on Monthly Shifts)
    const totals = useMemo(() => {
        const acc = {
            ALL: 0,       // Net Balance (Day + Night + Bilingual + Tips + Extra - Expense)
            NIGHT: 0,     // Night Base + Night Bonus
            DAY: 0,       // Day Base
            BILINGUAL: 0, // Bilingual Bonus
            TIPS: 0,      // Total Tips
            EXTRA: 0,     // Total Extra
            EXPENSE: 0,   // Total Expenses
            FEES: 0,      // Total Advance Fees (Taxas)
            OFF: 0,       // Count
            NO_SHOW: 0,   // Count
            UNPAID_BREAK: 0 // Total unrested minutes
        };

        monthlyShifts.forEach(s => {
            const appTips = s.appTips || 0;
            const outsideTips = s.outsideTips || (s.appTips === undefined ? (s as any).tips || 0 : 0);
            const totalTips = appTips + outsideTips;
            const extra = s.extraEarnings ? s.extraEarnings.reduce((a, b) => a + b.amount, 0) : 0;
            const expense = s.expenses ? s.expenses.reduce((a, b) => a + b.amount, 0) : 0;

            // Accumulate non-hourly specific earnings regardless of type
            acc.TIPS += totalTips;
            acc.EXTRA += extra;
            acc.EXPENSE += expense;
            acc.UNPAID_BREAK += (s.unpaidBreakMinutes || 0);

            if (s.type === 'WORK') {
                const rhinoEarnings = s.totalEarnings || 0; // Contains Base + Night Premium + Bilingual
                const bilingualPay = s.breakdown?.bilingualPay || 0;
                const nightBonus = s.breakdown?.nightPay || 0;

                // Breakdown for specific cards
                // Safety check for time data
                let dayHours = 0;
                let nightHours = 0;
                if (s.startTime && s.endTime) {
                    const breakdown = calculateHoursBreakdown(s.date, s.startTime, s.endTime);
                    dayHours = breakdown.dayHours;
                    nightHours = breakdown.nightHours;
                }

                const dayBaseValue = dayHours * RATE_BASE;
                const nightBaseValue = nightHours * RATE_BASE;

                acc.DAY += dayBaseValue;
                acc.NIGHT += (nightBaseValue + nightBonus);
                acc.BILINGUAL += bilingualPay;

                // Geral part for WORK shifts
                const restValue = calculateRestValue(s.unpaidBreakMinutes || 0);
                acc.ALL += (rhinoEarnings + totalTips + extra + restValue) - expense;
            } else {
                // Non-WORK shifts (OFF/NO_SHOW) only count their extras/expenses for the Geral balance
                acc.ALL += (extra - expense);

                if (s.type === 'OFF') acc.OFF += 1;
                if (s.type === 'NO_SHOW') acc.NO_SHOW += 1;

                // Check for Fees in OFF shifts (Adiantamentos)
                if (s.expenses) {
                    const fees = s.expenses
                        .filter(e => e.description?.includes('Taxa Plific') || e.description?.includes('Custo de Adiantamento'))
                        .reduce((a, b) => a + b.amount, 0);
                    acc.FEES += fees;
                }
            }
        });

        return acc;
    }, [monthlyShifts]);

    // 4. Filter Configuration (The Unified Bar Items)
    const filterConfig = [
        {
            id: 'ALL',
            label: 'Geral',
            value: formatValue(totals.ALL),
            icon: Wallet,
            color: 'text-emerald-400',
            bg: 'bg-emerald-500/10',
            border: 'border-emerald-500/50',
            activeBg: 'bg-emerald-600',
            activeText: 'text-white'
        },
        {
            id: 'NIGHT',
            label: 'Noturno',
            value: formatValue(totals.NIGHT),
            icon: Moon,
            color: 'text-purple-400',
            bg: 'bg-purple-500/10',
            border: 'border-purple-500/50',
            activeBg: 'bg-purple-600',
            activeText: 'text-white'
        },
        {
            id: 'DAY',
            label: 'Diurno',
            value: formatValue(totals.DAY),
            icon: Sun,
            color: 'text-yellow-400',
            bg: 'bg-yellow-500/10',
            border: 'border-yellow-500/50',
            activeBg: 'bg-yellow-600',
            activeText: 'text-white'
        },
        {
            id: 'BILINGUAL',
            label: 'Bilíngue',
            value: `R$ ${totals.BILINGUAL.toFixed(0)}`,
            icon: Globe,
            color: 'text-cyan-400',
            bg: 'bg-cyan-500/10',
            border: 'border-cyan-500/50',
            activeBg: 'bg-cyan-600',
            activeText: 'text-white'
        },
        {
            id: 'TIPS',
            label: 'Gorjetas',
            value: `R$ ${totals.TIPS.toFixed(0)}`,
            icon: Coins,
            color: 'text-amber-400',
            bg: 'bg-amber-500/10',
            border: 'border-amber-500/50',
            activeBg: 'bg-amber-600',
            activeText: 'text-white'
        },
        {
            id: 'EXTRA',
            label: 'Extras',
            value: `R$ ${totals.EXTRA.toFixed(0)}`,
            icon: TrendingUp,
            color: 'text-blue-400',
            bg: 'bg-blue-500/10',
            border: 'border-blue-500/50',
            activeBg: 'bg-blue-600',
            activeText: 'text-white'
        },
        {
            id: 'EXPENSE',
            label: 'Gastos',
            value: `R$ ${totals.EXPENSE.toFixed(0)}`,
            icon: ShoppingCart,
            color: 'text-red-400',
            bg: 'bg-red-500/10',
            border: 'border-red-500/50',
            activeBg: 'bg-red-600',
            activeText: 'text-white'
        },
        {
            id: 'NO_SHOW',
            label: 'Faltas',
            value: `${totals.NO_SHOW}`,
            icon: AlertTriangle,
            color: 'text-red-400',
            bg: 'bg-red-500/10',
            border: 'border-red-500/50',
            activeBg: 'bg-red-600',
            activeText: 'text-white'
        },
        {
            id: 'FEES',
            label: 'Taxas',
            value: `R$ ${totals.FEES.toFixed(0)}`,
            icon: Percent,
            color: 'text-orange-400',
            bg: 'bg-orange-500/10',
            border: 'border-orange-500/50',
            activeBg: 'bg-orange-600',
            activeText: 'text-white'
        },
        {
            id: 'OFF',
            label: 'Folgas',
            value: `${totals.OFF}`,
            icon: Coffee,
            color: 'text-slate-400',
            bg: 'bg-slate-700/30',
            border: 'border-slate-500/50',
            activeBg: 'bg-slate-600',
            activeText: 'text-white'
        },
        {
            id: 'UNPAID_BREAK',
            label: 'Min. Salvados',
            value: `${totals.UNPAID_BREAK} min`,
            icon: Timer,
            color: 'text-orange-400',
            bg: 'bg-orange-500/10',
            border: 'border-orange-500/50',
            activeBg: 'bg-orange-600',
            activeText: 'text-white'
        },
    ];

    // 5. Final Filter Logic (Apply clicked filter to the list)
    const displayedShifts = useMemo(() => {
        return monthlyShifts.filter(shift => {
            let dayHours = 0;
            let nightHours = 0;

            // Safety check for calculation
            if (shift.startTime && shift.endTime) {
                try {
                    const breakdown = calculateHoursBreakdown(shift.date, shift.startTime, shift.endTime);
                    dayHours = breakdown.dayHours;
                    nightHours = breakdown.nightHours;
                } catch (e) {
                    console.warn("Invalid shift times for filter:", shift);
                }
            }

            const expense = shift.expenses ? shift.expenses.reduce((a, b) => a + b.amount, 0) : 0;
            const extra = shift.extraEarnings ? shift.extraEarnings.reduce((a, b) => a + b.amount, 0) : 0;

            switch (filterType) {
                case 'OFF': return shift.type === 'OFF';
                case 'NO_SHOW': return shift.type === 'NO_SHOW';
                case 'TIPS': {
                    const totalT = (shift.appTips || 0) + (shift.outsideTips || (shift.appTips === undefined ? (shift as any).tips || 0 : 0));
                    return totalT > 0;
                }
                case 'BILINGUAL': return (shift.breakdown?.bilingualPay || 0) > 0;
                case 'NIGHT': return shift.type === 'WORK' && (nightHours > 0 || (shift.breakdown?.nightPay || 0) > 0);
                case 'DAY': return shift.type === 'WORK' && dayHours > 0;
                case 'EXTRA': return extra > 0;
                case 'EXPENSE': return expense > 0 && !shift.expenses?.some(e => e.description?.includes('Taxa Plific') || e.description?.includes('Custo de Adiantamento'));
                case 'FEES': return shift.expenses?.some(e => e.description?.includes('Taxa Plific') || e.description?.includes('Custo de Adiantamento')) || false;
                case 'UNPAID_BREAK': return (shift.unpaidBreakMinutes || 0) > 0;
                case 'ALL':
                default: return true;
            }
        });
    }, [monthlyShifts, filterType]);

    // 6. Group by Week
    const weeklyGroups = useMemo(() => {
        const groups: Record<string, WeeklyGroup> = {};

        displayedShifts.forEach(shift => {
            if (!shift.date) return;
            const date = parseDate(shift.date);
            const start = getStartOfWeek(date); // Using helper
            const key = start.toISOString();

            if (!groups[key]) {
                groups[key] = {
                    weekStart: start,
                    weekEnd: getEndOfWeek(date), // Using helper
                    shifts: [],
                    summaryValue: 0,
                    summaryHours: 0
                };
            }

            const extra = shift.extraEarnings ? shift.extraEarnings.reduce((a, b) => a + b.amount, 0) : 0;
            const expense = shift.expenses ? shift.expenses.reduce((a, b) => a + b.amount, 0) : 0;
            const totalT = (shift.appTips || 0) + (shift.outsideTips || (shift.appTips === undefined ? (shift as any).tips || 0 : 0));
            const restValue = calculateRestValue(shift.unpaidBreakMinutes || 0);
            const net = ((shift.totalEarnings || 0) + totalT + extra + restValue) - expense;

            groups[key].shifts.push(shift);
            groups[key].summaryValue += net;
            groups[key].summaryHours += (shift.totalHours || 0);
        });

        return Object.values(groups).sort((a, b) => b.weekStart.getTime() - a.weekStart.getTime());
    }, [displayedShifts]);


    // Helper for Card Styling
    const getCardStyle = (shift: Shift) => {
        if (shift.type === 'NO_SHOW') {
            return { border: 'border-red-500', bg: 'bg-red-900/10', icon: AlertTriangle, iconColor: 'text-red-500', text: 'text-red-200' };
        }
        if (shift.type === 'OFF') {
            // Special handling for Advance records which are created as 'OFF'
            const firstExpense = shift.expenses?.[0];
            if (firstExpense?.description?.includes('Adiantado')) {
                return { border: 'border-orange-500', bg: 'bg-orange-900/10', icon: ArrowDownCircle, iconColor: 'text-orange-500', text: 'text-orange-100' };
            }
            if (firstExpense?.description?.includes('Taxa Plific') || firstExpense?.description?.includes('Custo de Adiantamento')) {
                return { border: 'border-slate-600', bg: 'bg-slate-900/40', icon: FileText, iconColor: 'text-slate-500', text: 'text-slate-400' };
            }
            return { border: 'border-slate-500', bg: 'bg-slate-800', icon: Coffee, iconColor: 'text-slate-400', text: 'text-slate-300' };
        }

        let dayHours = 0;
        let nightHours = 0;
        if (shift.startTime && shift.endTime) {
            try {
                const breakdown = calculateHoursBreakdown(shift.date, shift.startTime, shift.endTime);
                dayHours = breakdown.dayHours;
                nightHours = breakdown.nightHours;
            } catch (e) {
                // ignore
            }
        }

        const isNight = nightHours > dayHours;

        if (isNight) {
            return { border: 'border-purple-500', bg: 'bg-purple-900/10', icon: Moon, iconColor: 'text-purple-400', text: 'text-purple-200' };
        }
        return { border: 'border-yellow-500', bg: 'bg-yellow-900/10', icon: Sun, iconColor: 'text-yellow-100' };
    };

    const confirmDelete = () => { if (shiftToDelete) { onDelete(shiftToDelete.id); setShiftToDelete(null); } };

    if (isLoading) {
        return (
            <div className="space-y-6 animate-pulse">
                <div className="h-10 bg-slate-800 rounded w-full"></div>
                <div className="space-y-3">{[...Array(3)].map((_, j) => (<div key={j} className="h-24 bg-slate-800 rounded-xl"></div>))}</div>
            </div>
        );
    }

    return (
        <div className="space-y-4 animate-fade-in relative pb-24 md:pb-0">

            {/* 1. Header: Month Selector */}
            <div id="tour-history" className="flex flex-col md:flex-row md:items-center justify-between gap-2 mb-2">
                <h2 className="text-xl font-bold text-white flex items-center gap-2"><Calendar className="w-5 h-5 text-orange-500" /> Histórico</h2>
                <select value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} className="bg-slate-800 border border-slate-700 text-white text-sm rounded-lg focus:ring-orange-500 focus:border-orange-500 p-2 outline-none w-full md:w-auto">
                    <option value="">Todo o Período</option>
                    {availableMonths.map(m => { const [y, monthIdx] = m.split('-'); const date = new Date(parseInt(y), parseInt(monthIdx) - 1); const label = format(date, 'MMMM yyyy', { locale: ptBR }); return <option key={m} value={m}>{label.charAt(0).toUpperCase() + label.slice(1)}</option> })}
                </select>
            </div>

            {/* 2. Unified Metrics Bar (Scrollable Container) */}
            <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-hide whitespace-nowrap">
                <style>{` .scrollbar-hide::-webkit-scrollbar { display: none; } `}</style>
                {filterConfig.map((item) => {
                    const isActive = filterType === item.id;
                    const Icon = item.icon;

                    return (
                        <button
                            key={item.id}
                            onClick={() => setFilterType(item.id as FilterType)}
                            className={`
                        flex-shrink-0 flex flex-col justify-between p-3 rounded-xl border min-w-[105px] h-[82px] transition-all duration-300 relative overflow-hidden group
                        ${isActive
                                    ? `${item.activeBg} border-transparent shadow-lg scale-100 z-10`
                                    : `${item.bg} ${item.border} hover:border-opacity-100 hover:bg-opacity-20`
                                }
                    `}
                        >
                            <div className="flex justify-between items-start w-full z-10">
                                <Icon className={`w-5 h-5 ${isActive ? 'text-white' : item.color}`} />
                                {isActive && <CheckCircle2 className="w-4 h-4 text-white/80" />}
                            </div>

                            <div className="text-left z-10 mt-1">
                                <span className={`text-[10px] font-bold uppercase tracking-wider block mb-0.5 ${isActive ? 'text-white/80' : 'text-slate-400'}`}>
                                    {item.label}
                                </span>
                                <span className={`text-base font-bold leading-tight ${isActive ? 'text-white' : 'text-white'}`}>
                                    {item.value}
                                </span>
                            </div>

                            {/* Subtle Glow Effect on Active */}
                            {isActive && <div className="absolute -right-4 -bottom-4 w-16 h-16 bg-white/10 rounded-full blur-xl"></div>}
                        </button>
                    );
                })}
            </div>

            {/* 3. Shift List (Cards) */}
            <div key={selectedMonth || 'all'} className="space-y-6 animate-fade-in">
                {weeklyGroups.length === 0 ? (
                    <div className="text-center py-10 bg-slate-800/30 rounded-xl border border-dashed border-slate-700">
                        <div className="bg-slate-800 inline-block p-4 rounded-full mb-4"><FilterX className="w-8 h-8 text-slate-500" /></div>
                        <h3 className="text-lg font-medium text-white">Nenhum registro</h3>
                        <p className="text-slate-500 text-sm mt-1">Nenhum turno encontrado neste filtro.</p>
                    </div>
                ) : (
                    weeklyGroups.map((group) => {
                        const startLabel = format(group.weekStart, "d MMM", { locale: ptBR });
                        const endLabel = format(group.weekEnd, "d MMM", { locale: ptBR });

                        return (
                            <div key={group.weekStart.toISOString()} className="animate-fade-in">
                                <div className="flex justify-between items-center border-b border-slate-700/50 pb-3 mb-4 px-1">
                                    <div className="flex flex-col">
                                        <h3 className="text-white font-black text-lg tracking-tight uppercase">
                                            {startLabel} — {endLabel}
                                        </h3>
                                        <span className="text-[10px] text-orange-500/80 font-bold uppercase tracking-[0.2em]">Semana Fechada</span>
                                    </div>
                                    <div className="bg-slate-800/50 px-3 py-1.5 rounded-lg border border-slate-700/50">
                                        <span className="text-xs text-slate-400 font-medium">Total: </span>
                                        <span className="text-sm font-bold text-white">{formatValue(group.summaryValue, 2)}</span>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {group.shifts.map(shift => {
                                        const style = getCardStyle(shift);
                                        const extraTotal = shift.extraEarnings ? shift.extraEarnings.reduce((a, b) => a + b.amount, 0) : 0;
                                        const expenseTotal = shift.expenses ? shift.expenses.reduce((a, b) => a + b.amount, 0) : 0;
                                        const appT = shift.appTips || 0;
                                        const outT = shift.outsideTips || (shift.appTips === undefined ? (shift as any).tips || 0 : 0);
                                        const totalT = appT + outT;
                                        const restVal = calculateRestValue(shift.unpaidBreakMinutes || 0);
                                        const net = ((shift.totalEarnings || 0) + totalT + extraTotal + restVal) - expenseTotal;

                                        // Value to display based on filter context
                                        let displayValue = net;
                                        let valueLabel = 'Líquido';

                                        let dayHours = 0;
                                        let nightHours = 0;

                                        if (shift.startTime && shift.endTime) {
                                            try {
                                                const breakdown = calculateHoursBreakdown(shift.date, shift.startTime, shift.endTime);
                                                dayHours = breakdown.dayHours;
                                                nightHours = breakdown.nightHours;
                                            } catch (e) {
                                                // Ignore
                                            }
                                        }

                                        if (filterType === 'NIGHT') {
                                            const nightBaseValue = nightHours * RATE_BASE;
                                            const nightBonus = shift.breakdown?.nightPay || 0;
                                            displayValue = nightBaseValue + nightBonus;
                                            valueLabel = 'Noturno';
                                        } else if (filterType === 'DAY') {
                                            displayValue = dayHours * RATE_BASE;
                                            valueLabel = 'Diurno';
                                        } else if (filterType === 'BILINGUAL') {
                                            displayValue = shift.breakdown?.bilingualPay || 0;
                                            valueLabel = 'Bônus';
                                        } else if (filterType === 'TIPS') {
                                            displayValue = totalT;
                                            valueLabel = 'Gorjetas';
                                        } else if (filterType === 'EXTRA') {
                                            displayValue = extraTotal;
                                            valueLabel = 'Renda Extra';
                                        } else if (filterType === 'EXPENSE') {
                                            displayValue = expenseTotal;
                                            valueLabel = 'Despesas';
                                        }

                                        const bilingualHours = (shift.bilingualStart && shift.bilingualEnd)
                                            ? getDurationInHours(shift.bilingualStart, shift.bilingualEnd)
                                            : 0;

                                        const isPlificAdvance = shift.type === 'OFF' && shift.expenses?.some(e => e.description?.toLowerCase().includes('adiantamento') || e.description?.toLowerCase().includes('adiantado'));

                                        if (isPlificAdvance) {
                                            const netExpense = shift.expenses?.find(e => e.description?.toLowerCase().includes('adiantamento') || e.description?.toLowerCase().includes('adiantado'));
                                            const feeExpense = shift.expenses?.find(e => e.description?.toLowerCase().includes('taxa plific') || e.description?.toLowerCase().includes('custo de adiantamento'));
                                            const totalAmount = (netExpense?.amount || 0) + (feeExpense?.amount || 0);

                                            return (
                                                <div key={shift.id} className="relative flex flex-col p-4 rounded-xl border-l-4 border-orange-500 bg-orange-950/10 bg-opacity-40 border-t border-r border-b border-slate-700/50 shadow-sm transition-all hover:bg-opacity-60">
                                                    <div className="flex justify-between items-start mb-3">
                                                        <div className="flex gap-3 items-center">
                                                            <div className="p-2 rounded-full bg-slate-900/40 text-orange-500 border border-white/5">
                                                                <Wallet className="w-5 h-5" />
                                                            </div>
                                                            <div>
                                                                <p className="font-bold text-white text-base capitalize">
                                                                    {format(parseDate(shift.date), "EEE, dd/MM", { locale: ptBR })}
                                                                </p>
                                                                <div className="text-xs text-orange-400 font-bold bg-orange-900/20 px-1.5 py-0.5 rounded border border-orange-500/20 mt-1 w-fit">
                                                                    Adiantamento Plific
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div className="text-right">
                                                            <span className="text-lg font-bold block text-red-400">
                                                                -{formatValue(totalAmount, 2)}
                                                            </span>
                                                            <span className="text-[10px] text-slate-500 uppercase">Dedução Total</span>
                                                        </div>
                                                    </div>

                                                    {/* Breakdown of Net and Fee */}
                                                    <div className="grid grid-cols-2 gap-2 text-xs text-slate-400 bg-slate-900/40 p-2.5 rounded-lg border border-slate-800/50">
                                                        <div className="flex flex-col">
                                                            <span className="text-[10px] text-slate-500 uppercase font-semibold">Valor Recebido</span>
                                                            <span className="font-bold text-white mt-0.5">{formatValue(netExpense?.amount || 0, 2)}</span>
                                                        </div>
                                                        <div className="flex flex-col text-right">
                                                            <span className="text-[10px] text-slate-500 uppercase font-semibold">Taxa Plific (4%)</span>
                                                            <span className="font-bold text-red-400 mt-0.5">{formatValue(feeExpense?.amount || 0, 2)}</span>
                                                        </div>
                                                    </div>

                                                    {/* Action Footer */}
                                                    <div className="mt-2 pt-2 border-t border-white/5 flex justify-end gap-2">
                                                        <button onClick={() => onDuplicate(shift)} className="p-2 text-slate-400 hover:text-white bg-slate-800 rounded-lg hover:bg-slate-700 transition-colors" title="Duplicar"><Copy className="w-3.5 h-3.5" /></button>
                                                        <button onClick={() => onEdit(shift)} className="p-2 text-slate-400 hover:text-white bg-slate-800 rounded-lg hover:bg-slate-700 transition-colors" title="Editar"><Pencil className="w-3.5 h-3.5" /></button>
                                                        <button onClick={() => setShiftToDelete(shift)} className="p-2 text-slate-400 hover:text-white bg-slate-800 rounded-lg hover:bg-slate-700 transition-colors" title="Excluir"><Trash2 className="w-3.5 h-3.5" /></button>
                                                    </div>
                                                </div>
                                            );
                                        }

                                        return (
                                            <div key={shift.id} className={`
                                    relative flex flex-col p-4 rounded-xl border-l-4 ${style.border} ${style.bg} 
                                    bg-opacity-40 border-t border-r border-b border-slate-700/50 shadow-sm transition-all hover:bg-opacity-60
                                `}>
                                                <div className="flex justify-between items-start mb-2">
                                                    <div className="flex gap-3 items-center">
                                                        <div className={`p-2 rounded-full bg-slate-900/40 ${style.iconColor} border border-white/5`}>
                                                            <style.icon className="w-5 h-5" />
                                                        </div>
                                                        <div>
                                                            <p className="font-bold text-white text-base capitalize">{format(parseDate(shift.date), "EEE, dd/MM", { locale: ptBR })}</p>
                                                            {shift.type === 'OFF' && expenseTotal > 0 && (
                                                                <div className="text-xs text-slate-400 font-medium mt-1">
                                                                    {shift.expenses?.[0]?.description || 'Despesa Administrativa'}
                                                                </div>
                                                            )}
                                                            {shift.type === 'WORK' && (
                                                                <div className="flex flex-col gap-1 mt-0.5">
                                                                    <div className="flex items-center gap-2">
                                                                        <span className="text-xs text-slate-400 font-mono bg-slate-900/30 px-1.5 rounded">
                                                                            {shift.startTime || '--:--'} - {shift.endTime || '--:--'}
                                                                        </span>
                                                                        {shift.plannedStartTime && (
                                                                            <span className="text-[9px] text-orange-400/80 font-mono bg-orange-900/20 px-1.5 rounded border border-orange-500/30" title="Horário Planejado Original">
                                                                                P: {shift.plannedStartTime} - {shift.plannedEndTime}
                                                                            </span>
                                                                        )}
                                                                        {filterType === 'ALL' && (
                                                                            <span className="text-[9px] text-slate-400/70 border border-slate-700/50 px-1 py-0.5 rounded uppercase">
                                                                                {(shift.generation || userGeneration) === 'NEW' ? 'Geração Nova' : 'Geração Antiga'}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    {shift.vehiclePlate && (
                                                                        <div className="flex items-center gap-1 text-[10px] text-slate-400 font-mono">
                                                                            <div className="w-1.5 h-1.5 rounded-full bg-slate-600"></div>
                                                                            Placa: <span className="text-slate-300 font-bold uppercase">{shift.vehiclePlate}</span>
                                                                        </div>
                                                                    )}
                                                                    {shift.carArrivalTime && shift.plannedStartTime && (toMin(shift.carArrivalTime) > toMin(shift.plannedStartTime)) && (
                                                                        <div className="flex items-center gap-1.5 text-[9px] text-emerald-400 font-bold bg-emerald-950/20 px-2 py-1 rounded border border-emerald-900/30 w-fit">
                                                                            <Truck className="w-3 h-3" />
                                                                            <span>Atraso Logístico ({shift.carArrivalTime})</span>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>

                                                    <div className="text-right">
                                                        {(shift.type !== 'NO_SHOW' && shift.type !== 'OFF') || expenseTotal > 0 ? (
                                                            <>
                                                                <span className={`text-lg font-bold block ${(filterType === 'EXPENSE' || expenseTotal > 0) ? 'text-red-400' : 'text-white'}`}>
                                                                    {formatValue(Math.abs(displayValue), 2)}
                                                                </span>
                                                                {(filterType !== 'ALL' || shift.type === 'OFF') && <span className="text-[10px] text-slate-500 uppercase">{valueLabel}</span>}
                                                            </>
                                                        ) : (
                                                            <span className="text-sm italic text-slate-500 py-1 block">{shift.type === 'OFF' ? 'Descanso' : 'Ausente'}</span>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* DETAILED METRICS FOR "ALL" FILTER */}
                                                {filterType === 'ALL' && shift.type === 'WORK' && (
                                                    <div className="mt-3 mb-1 grid grid-cols-2 gap-2 text-xs text-slate-400 bg-slate-900/40 p-2 rounded-lg border border-slate-800/50">
                                                        {/* Day Hours */}
                                                        <div className="flex items-center gap-1.5" title="Horas Diurnas">
                                                            <Sun className="w-3.5 h-3.5 text-yellow-500" />
                                                            <span className="font-medium text-slate-300">{formatHoursToHHMM(dayHours)}h</span>
                                                        </div>
                                                        {/* Night Hours */}
                                                        <div className="flex items-center gap-1.5" title="Horas Noturnas">
                                                            <Moon className="w-3.5 h-3.5 text-purple-400" />
                                                            <span className="font-medium text-slate-300">{formatHoursToHHMM(nightHours)}h</span>
                                                        </div>
                                                        {/* Bilingual Hours */}
                                                        {bilingualHours > 0 && (
                                                            <div className="flex items-center gap-1.5" title="Horas Bilíngues">
                                                                <Globe className="w-3.5 h-3.5 text-cyan-400" />
                                                                <span className="font-medium text-slate-300">{formatHoursToHHMM(bilingualHours)}h | R$ {(shift.breakdown?.bilingualPay || 0).toFixed(0)}</span>
                                                            </div>
                                                        )}
                                                        {/* Expenses */}
                                                        {expenseTotal > 0 && (
                                                            <div className="flex items-center gap-1.5" title="Total Gastos">
                                                                <ShoppingCart className="w-3.5 h-3.5 text-red-400" />
                                                                <span className="font-medium text-red-300">-R${expenseTotal.toFixed(0)}</span>
                                                            </div>
                                                        )}
                                                        {/* Minutos Não Descansados */}
                                                        {(shift.unpaidBreakMinutes && shift.unpaidBreakMinutes > 0) ? (
                                                            <div className="group relative col-span-2">
                                                                <div className="flex items-center gap-1.5 text-emerald-400 font-bold bg-emerald-950/20 px-2 py-1 rounded border border-emerald-900/30 cursor-help" title="Pausa Salva Convertida">
                                                                    <Clock className="w-3.5 h-3.5" />
                                                                    <span>Pausa Salva: {shift.unpaidBreakMinutes} min (+ R$ {calculateRestValue(shift.unpaidBreakMinutes).toFixed(2).replace('.', ',')})</span>
                                                                    <div className="ml-auto w-4 h-4 rounded-full bg-emerald-900/50 flex items-center justify-center text-[10px] text-emerald-300">?</div>
                                                                </div>
                                                                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 p-3 bg-slate-800 border border-slate-700 rounded-lg shadow-xl text-[10px] font-normal leading-relaxed text-slate-300 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 pointer-events-none">
                                                                    Conversão em Hora Centesimal ({shift.unpaidBreakMinutes}m = {((shift.unpaidBreakMinutes / 60) * 100).toFixed(0)} centesimais) a R$ 0,20 por unidade.
                                                                    <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-[6px] border-transparent border-t-slate-700"></div>
                                                                </div>
                                                            </div>
                                                        ) : null}
                                                    </div>
                                                )}

                                                {shift.type === 'WORK' && (
                                                    <div className="mt-2.5">
                                                        <button
                                                            onClick={() => toggleDetails(shift.id)}
                                                            className="w-full flex items-center justify-between text-[10px] font-bold text-indigo-400 hover:text-indigo-300 bg-indigo-950/20 border border-indigo-900/30 rounded-lg py-1.5 px-2.5 transition-colors animate-fade-in"
                                                        >
                                                            <span className="flex items-center gap-1">📋 Ver Detalhes do Turno</span>
                                                            <span>{expandedDetails[shift.id] ? '▲ Ocultar' : '▼ Expandir'}</span>
                                                        </button>

                                                        {expandedDetails[shift.id] && (
                                                            <div className="mt-2 p-2.5 bg-slate-900/60 border border-slate-700/60 rounded-lg space-y-2 animate-in slide-in-from-top-1 duration-200">
                                                                <div className="text-[9px] text-slate-400 font-bold border-b border-slate-800 pb-1 flex justify-between items-center">
                                                                    <span>Logística Real da Escala</span>
                                                                    <span className="text-indigo-400">Auditoria</span>
                                                                </div>
                                                                <div className="grid grid-cols-2 gap-2 text-[9px] text-slate-400 font-mono">
                                                                    <div className="flex flex-col p-1 bg-slate-950/20 rounded border border-slate-800/40">
                                                                        <span className="text-slate-500 font-sans font-semibold">1. Horário Programado:</span>
                                                                        <span className="text-slate-300 font-bold mt-0.5">{shift.plannedStartTime || shift.startTime}</span>
                                                                    </div>
                                                                    <div className="flex flex-col p-1 bg-slate-950/20 rounded border border-slate-800/40">
                                                                        <span className="text-slate-500 font-sans font-semibold">2. Chegada do Carro:</span>
                                                                        <span className="text-slate-300 font-bold mt-0.5">{shift.carArrivalTime || '--:--'}</span>
                                                                    </div>
                                                                    <div className="flex flex-col p-1 bg-slate-950/20 rounded border border-slate-800/40">
                                                                        <span className="text-slate-500 font-sans font-semibold">3. Login Telegram:</span>
                                                                        <span className="text-slate-300 font-bold mt-0.5">{shift.loginTelegramTime || '--:--'}</span>
                                                                    </div>
                                                                    <div className="flex flex-col p-1 bg-slate-950/20 rounded border border-slate-800/40">
                                                                        <span className="text-slate-500 font-sans font-semibold">4. Logout Telegram:</span>
                                                                        <span className="text-amber-400 font-bold mt-0.5">{shift.logoutTelegramTime || '--:--'}</span>
                                                                    </div>
                                                                    <div className="flex flex-col p-1 bg-slate-950/20 rounded border border-slate-800/40">
                                                                        <span className="text-slate-500 font-sans font-semibold">5. Login Driver App:</span>
                                                                        <span className="text-slate-300 font-bold mt-0.5">{shift.loginAppTime || '--:--'}</span>
                                                                    </div>
                                                                    <div className="flex flex-col p-1 bg-slate-950/20 rounded border border-slate-800/40">
                                                                        <span className="text-slate-500 font-sans font-semibold">6. Fim Real (Deputy):</span>
                                                                        <span className="text-emerald-400 font-bold mt-0.5">
                                                                            {shift.endTime} ({shift.unpaidBreakMinutes || 0}m descanso)
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                                <p className="text-[8px] text-slate-500 bg-slate-950/40 p-1.5 rounded border border-slate-800/60 italic leading-relaxed text-center font-sans">
                                                                    ℹ️ <strong>Dica WhatsApp:</strong> Lembra que os horários do Telegram e do Driver App podem ser checados na mensagem de Login/Token recebida no seu WhatsApp.
                                                                </p>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                {/* Action Footer */}
                                                <div className="mt-2 pt-2 border-t border-white/5 flex justify-end gap-2">
                                                    {shift.type === 'WORK' && (
                                                        <button onClick={() => setShiftToAdjust(shift)} className="p-2 text-emerald-400 hover:text-white bg-emerald-900/30 rounded-lg hover:bg-emerald-800/50 transition-colors border border-emerald-800/30" title="Ajuste Real">
                                                            <ClipboardCheck className="w-3.5 h-3.5" />
                                                        </button>
                                                    )}
                                                    <button onClick={() => onDuplicate(shift)} className="p-2 text-slate-400 hover:text-white bg-slate-800 rounded-lg hover:bg-slate-700 transition-colors" title="Duplicar"><Copy className="w-3.5 h-3.5" /></button>
                                                    <button onClick={() => onEdit(shift)} className="p-2 text-slate-400 hover:text-white bg-slate-800 rounded-lg hover:bg-slate-700 transition-colors" title="Editar"><Pencil className="w-3.5 h-3.5" /></button>
                                                    <button onClick={() => setShiftToDelete(shift)} className="p-2 text-slate-400 hover:text-white bg-slate-800 rounded-lg hover:bg-slate-700 transition-colors" title="Excluir"><Trash2 className="w-3.5 h-3.5" /></button>
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {shiftToDelete && (
                <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 backdrop-blur-sm animate-fade-in">
                    <div className="bg-slate-800 p-6 rounded-2xl border border-slate-700 shadow-2xl max-w-sm w-full">
                        <div className="flex flex-col items-center text-center mb-6">
                            <div className="bg-red-900/30 p-3 rounded-full mb-4"><AlertTriangle className="w-8 h-8 text-red-500" /></div>
                            <h3 className="text-xl font-bold text-white mb-2">Excluir Registro?</h3>
                            <p className="text-slate-400 text-sm">Isso removerá o turno de <span className="font-bold text-white">{format(parseDate(shiftToDelete.date), "dd/MM", { locale: ptBR })}</span>.</p>
                        </div>
                        <div className="flex gap-3"><button onClick={() => setShiftToDelete(null)} className="flex-1 bg-slate-700 hover:bg-slate-600 text-white font-medium py-3 rounded-xl transition-colors">Cancelar</button><button onClick={confirmDelete} className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold py-3 rounded-xl transition-colors shadow-lg shadow-red-900/20">Excluir</button></div>
                    </div>
                </div>
            )}


            {shiftToAdjust && (
                <AdjustShiftModal
                    shift={shiftToAdjust}
                    onClose={() => setShiftToAdjust(null)}
                    onConfirm={(adjusted) => {
                        if (onConfirmAdjust) {
                            onConfirmAdjust(adjusted);
                        } else {
                            onEdit(adjusted);
                        }
                        setShiftToAdjust(null);
                    }}
                    parseDate={parseDate}
                />
            )}
        </div>
    );
};
